import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import IgnoredView from './IgnoredView';
import { apiErrorReason, apiStatus, fetchAdvisors } from './api';
import {
  EMPTY_FILTERS,
  advisorPagePath,
  flagReasons,
  isIgnoredView,
  listQuery,
  rangeFromSearch,
  searchWithView
} from './advisors';
import type { AdvisorsView, DayRange } from './advisors';
import { PLAIN_PILL, SUCCESS_PILL, WARN_PILL } from './pills';
import { Btn, LABEL_CLASS, Segmented, btnStyle } from '../ui';
import StackTd from '../StackTable';
import { stackClasses } from '../stack_table';
import type { AdvisorSummary } from './types';

export type Notify = (type: 'success' | 'error', message: string) => void;

interface Props {
  notify: Notify;
  // Deep link (?view=ignored&from=&to=); falls back to the page URL when unset.
  initialView?: AdvisorsView;
  initialRange?: DayRange;
  // can_write_advisors (/me); false keeps the Not-assigned view read-only.
  canWrite: boolean;
}

// Widths fit the 1024px viewport with the sidebar open; below md rows become cards.
const SUMMARY_COLS = [
  { label: 'Advisor', width: 'w-[20%]' },
  { label: 'Level', width: 'w-[12%]' },
  { label: 'Roster', width: 'w-[10%]' },
  { label: 'SALES role', width: 'w-[10%]' },
  { label: 'Active students', width: 'w-[11%]' },
  { label: 'Flags', width: '' },
  { label: '', width: 'w-[172px]' }
];
const CELL = 'px-3.5 py-2.5 text-left text-[13px] font-normal';
const ST = stackClasses();

const dash = (v: string | null | undefined) => v || '—';

const pageSearch = () => (typeof window === 'undefined' ? '' : window.location.search);

// Summary of every advisor; each one's students open on their own page in a new tab.
function AdvisorsPanel({ notify, initialView, initialRange, canWrite }: Props) {
  const router = useHistory();
  const [view, setView] = useState<AdvisorsView>(
    initialView || (isIgnoredView(pageSearch()) ? 'ignored' : 'assignments')
  );
  const [range, setRange] = useState<DayRange>(() => initialRange || rangeFromSearch(pageSearch()));
  // Ignored-tag rows outlive an assign; remember who was assigned while the Advisors tab is open (REQ 57).
  const [assignedUuids, setAssignedUuids] = useState<ReadonlySet<string>>(new Set());
  const [summary, setSummary] = useState<AdvisorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<{ forbidden: boolean; reason: string } | null>(null);
  const loadSeq = useRef(0);

  const load = useCallback(() => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setLoading(true);
    // limit=1: only the summary is needed here, the rows live on the advisor page.
    return fetchAdvisors(listQuery(EMPTY_FILTERS, 0, 1))
      .then((d) => {
        if (seq !== loadSeq.current) return;
        setSummary(d.summary);
        setLoadError(null);
      })
      .catch((e: unknown) => {
        if (seq !== loadSeq.current) return;
        const forbidden = apiStatus(e) === 403;
        const reason = forbidden
          ? 'Super user or sales manager only'
          : apiErrorReason(e, 'Failed to load advisors');
        setSummary([]);
        setLoadError({ forbidden, reason });
        if (!forbidden) notify('error', reason);
      })
      .finally(() => {
        if (seq === loadSeq.current) setLoading(false);
      });
  }, [notify]);

  // The Not-assigned view loads its own data; skip the unused summary call.
  useEffect(() => {
    if (view === 'ignored') return;
    load();
  }, [load, view]);

  // Keeps ?view=&from=&to= in the URL so a refresh or shared link lands on the same view.
  const syncUrl = (nextView: AdvisorsView, nextRange: DayRange) => {
    if (!router) return;
    const { pathname, search } = router.location;
    const next = searchWithView(search, nextView, nextRange);
    if (next !== search) router.replace({ pathname, search: next });
  };

  const changeView = (next: AdvisorsView) => {
    setView(next);
    syncUrl(next, range);
  };

  const changeRange = (next: DayRange) => {
    setRange(next);
    syncUrl('ignored', next);
  };

  const renderEmpty = () => {
    if (loading) return 'Loading advisors…';
    if (!loadError) return 'No advisors yet.';
    if (loadError.forbidden) return loadError.reason;
    return (
      <>
        {`Summary unavailable: ${loadError.reason} `}
        <Btn variant="outline" size="row" onClick={load} style={{ marginLeft: 8 }}>
          Retry
        </Btn>
      </>
    );
  };

  const renderRow = (s: AdvisorSummary) => (
    <tr
      key={s.cms_user_uuid}
      className={`border-t border-line-subtle text-[12.5px] hover:bg-surface-sunken ${ST.row}`}
    >
      <StackTd className={`${CELL} truncate`} title={s.name}>
        <a
          href={advisorPagePath(s.cms_user_uuid)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-content-link hover:underline"
        >
          {s.name}
        </a>
      </StackTd>
      <StackTd label="Level" className={`${CELL} truncate`} title={s.level || undefined}>
        {dash(s.level)}
      </StackTd>
      <StackTd label="Roster" className={CELL}>
        <span className={s.roster_status === 'LEFT' ? WARN_PILL : SUCCESS_PILL}>
          {s.roster_status === 'LEFT' ? 'Left' : 'Active'}
        </span>
      </StackTd>
      <StackTd label="SALES role" className={CELL}>
        <span className={s.has_sales ? PLAIN_PILL : WARN_PILL}>{s.has_sales ? 'Yes' : 'No'}</span>
      </StackTd>
      <StackTd
        label="Active students"
        className={`px-3.5 py-2.5 text-right tabular-nums ${s.active_students === 0 ? 'text-content-secondary' : ''}`}
      >
        {s.active_students}
      </StackTd>
      <StackTd label="Flags" className={CELL}>
        {s.flagged ? (
          <div className="flex flex-wrap gap-1">
            {flagReasons(s).map((r) => (
              <span key={r} className={WARN_PILL}>
                {r}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-content-muted">—</span>
        )}
      </StackTd>
      <StackTd className={CELL}>
        <a
          href={advisorPagePath(s.cms_user_uuid)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View students of ${s.name}`}
          className="no-underline hover:no-underline"
          style={btnStyle('neutral', 'row')}
        >
          Open students ↗
        </a>
      </StackTd>
    </tr>
  );

  const renderSummary = () => (
    <table className={ST.table} aria-label="Advisors summary">
      <thead className={`border-b border-line-subtle bg-surface-sunken ${ST.head}`}>
        <tr>
          {SUMMARY_COLS.map((c) => (
            <th
              key={c.label || 'action'}
              className={`px-3.5 py-2 ${LABEL_CLASS} ${c.width} ${c.label === 'Active students' ? 'text-right' : 'text-left'}`}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className={ST.body}>
        {summary.length === 0 ? (
          <tr className={ST.row}>
            <StackTd
              colSpan={SUMMARY_COLS.length}
              className="px-4 py-6 text-center text-content-muted"
            >
              {renderEmpty()}
            </StackTd>
          </tr>
        ) : (
          summary.map(renderRow)
        )}
      </tbody>
    </table>
  );

  return (
    <section
      className="overflow-hidden rounded-[10px] border border-line-subtle bg-surface-card text-[13px] text-content-primary"
      style={{ margin: '20px 5%' }}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5 border-b border-line-subtle px-3.5 py-3">
        <div className="min-w-0 flex-1 basis-[320px]">
          <h2 className="m-0 text-[15px] font-semibold">Advisors</h2>
          <p className="m-0 mt-0.5 text-xs text-content-muted">
            Each student has one sales advisor for 35 days after their first 1-on-1 note. Reassign
            students here, for example before removing someone&apos;s SALES role.{' '}
            <span>Times in IST</span>
          </p>
        </div>
        <Segmented<AdvisorsView>
          label="Advisors view"
          value={view}
          onChange={changeView}
          options={[
            { value: 'assignments', label: 'Assignments' },
            { value: 'ignored', label: 'Not assigned' }
          ]}
        />
      </div>
      {view === 'ignored' ? (
        <IgnoredView
          notify={notify}
          initialRange={range}
          onRangeChange={changeRange}
          assigned={assignedUuids}
          onAssigned={(uuid) => setAssignedUuids((prev) => new Set(prev).add(uuid))}
          canWrite={canWrite}
        />
      ) : (
        renderSummary()
      )}
    </section>
  );
}

export default AdvisorsPanel;
